import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  FileJson,
  ChevronLeft,
  ChevronRight,
  Upload,
  Zap,
  Eye,
  Trash2,
  RefreshCw,
  X,
  File,
  CheckCircle,
  AlertCircle,
  Clock,
} from "lucide-react";
import "./GFSV1WorkOrders.css";

const MODEL_CONFIG = {
  name: "GFS V1",
  description: "Textract + Qwen",
  color: "#3b82f6",
  bgColor: "#eff6ff",
};

const FORM_TYPES = [
  // Original GFS forms
  { value: "aof", label: "Account Opening Form (AOF)" },
  { value: "redemption", label: "Redemption (RP)" },
  { value: "additional_purchase", label: "Additional Purchase (AP)" },
  // TopUp Order forms
  { value: "topup_manulife_prs", label: "Manulife PRS Form" },
  { value: "topup_maybank_booking", label: "Maybank Booking Report" },
  { value: "topup_phillip_mutual", label: "Phillip Mutual Report" },
  { value: "topup_ifast_orders", label: "iFAST Cash Orders" },
  // TopUp Investment forms
  { value: "topup_epf_kwsp", label: "EPF KWSP 9N" },
];

// Custom Dropdown Component for Status Filter
const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="gfs-v1-custom-dropdown" ref={dropdownRef}>
      <button className="gfs-v1-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label}</span>
        <svg className={`dropdown-arrow ${isOpen ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="gfs-v1-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`gfs-v1-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [formType, setFormType] = useState("aof");
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(f => 
      f.name.toLowerCase().endsWith('.pdf') || 
      f.name.toLowerCase().endsWith('.tiff') ||
      f.name.toLowerCase().endsWith('.tif') ||
      f.name.toLowerCase().endsWith('.png') ||
      f.name.toLowerCase().endsWith('.jpg') ||
      f.name.toLowerCase().endsWith('.jpeg')
    );
    const existingNames = files.map(f => f.name);
    const uniqueFiles = validFiles.filter(f => !existingNames.includes(f.name));
    
    if (validFiles.length !== newFiles.length) {
      alert('Only PDF, TIFF, PNG, JPG files are supported.');
    }
    
    setFiles(prev => [...prev, ...uniqueFiles]);
    const newStatus = {};
    uniqueFiles.forEach(f => { newStatus[f.name] = 'pending'; });
    setUploadStatus(prev => ({ ...prev, ...newStatus }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.add('drag-over');
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
  };

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.name !== filename));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[filename];
      return newStatus;
    });
  };

  const handleUploadAll = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    const results = [];
    const token = localStorage.getItem("token");

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("form_type", formType);

        const res = await fetch("/v1/gfs/api/extract", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const data = await res.json();
        if (res.ok) {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
          results.push({ filename: file.name, success: true, work_order_id: data.work_order?.id });
        } else {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
          results.push({ filename: file.name, success: false, error: data.message || "Upload failed" });
        }
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    if (onUploadComplete) onUploadComplete(results);
  };

  const handleClose = () => {
    setFiles([]);
    setUploadStatus({});
    setUploadResults([]);
    setFormType("aof");
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading': return <RefreshCw size={16} className="spin" style={{ color: '#3b82f6' }} />;
      case 'success': return <CheckCircle size={16} style={{ color: '#22c55e' }} />;
      case 'error': return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
      default: return <Clock size={16} style={{ color: '#9ca3af' }} />;
    }
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => uploadStatus[f.name] === 'success' || uploadStatus[f.name] === 'error');
  const successCount = Object.values(uploadStatus).filter(s => s === 'success').length;
  const errorCount = Object.values(uploadStatus).filter(s => s === 'error').length;

  return (
    <div className="gfs-v1-modal-overlay" onClick={handleClose}>
      <div className="gfs-v1-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="gfs-v1-modal-header" style={{ borderBottomColor: MODEL_CONFIG.color }}>
          <h3>
            <Upload size={20} style={{ color: MODEL_CONFIG.color }} />
            Upload Files - {MODEL_CONFIG.name}
          </h3>
          <button className="gfs-v1-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="gfs-v1-modal-body">
          {/* Form Type Selection */}
          <div className="gfs-v1-form-type-section">
            <label>Form Type (Required for V1)</label>
            <select value={formType} onChange={(e) => setFormType(e.target.value)}>
              {FORM_TYPES.map((ft) => (
                <option key={ft.value} value={ft.value}>{ft.label}</option>
              ))}
            </select>
          </div>

          {/* Drop Zone */}
          <div
            ref={dropZoneRef}
            className="gfs-v1-drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} style={{ color: MODEL_CONFIG.color }} />
            <p>Drag & drop files here</p>
            <span>or click to browse (PDF, TIFF, PNG, JPG)</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.tiff,.tif,.png,.jpg,.jpeg"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="gfs-v1-file-list">
              <div className="gfs-v1-file-list-header">
                <span>{files.length} file(s) selected</span>
                {allUploaded && (
                  <span className="gfs-v1-upload-summary">
                    {successCount} success, {errorCount} failed
                  </span>
                )}
              </div>
              <div className="gfs-v1-file-items">
                {files.map(file => (
                  <div key={file.name} className={`gfs-v1-file-item ${uploadStatus[file.name]}`}>
                    <div className="gfs-v1-file-info">
                      <File size={16} />
                      <span className="gfs-v1-file-name" title={file.name}>
                        {file.name.length > 40 ? file.name.substring(0, 40) + '...' : file.name}
                      </span>
                      <span className="gfs-v1-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    {uploadStatus[file.name] === 'error' && uploadResults.find(r => r.filename === file.name)?.error && (
                      <div className="gfs-v1-file-error">
                        {uploadResults.find(r => r.filename === file.name)?.error}
                      </div>
                    )}
                    <div className="gfs-v1-file-actions">
                      {getStatusIcon(uploadStatus[file.name])}
                      {uploadStatus[file.name] === 'pending' && !uploading && (
                        <button className="gfs-v1-file-remove" onClick={() => removeFile(file.name)}>
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="gfs-v1-modal-footer">
          <button className="gfs-v1-btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button
              className="gfs-v1-btn-upload-all"
              style={{ backgroundColor: MODEL_CONFIG.color }}
              onClick={handleUploadAll}
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <><RefreshCw size={16} className="spin" /> Uploading...</>
              ) : (
                <><Upload size={16} /> Upload {files.length} File{files.length !== 1 ? 's' : ''}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const GFSV1WorkOrders = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10, skip: 0 });
  const [stats, setStats] = useState({ total: 0, completed: 0, processing: 0, pending: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async (skip = 0) => {
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const statusParam = statusFilter !== "all" ? `&status=${statusFilter}` : "";
      const [ordersRes, statsRes] = await Promise.all([
        fetch(`/v1/gfs/api/work-orders?skip=${skip}&limit=10&version=v1${statusParam}`, { headers }).then(r => r.json()),
        fetch("/v1/gfs/api/stats?version=v1", { headers }).then(r => r.json()),
      ]);

      if (ordersRes.work_orders) {
        setWorkOrders(ordersRes.work_orders);
        setPagination(ordersRes.pagination || { total: ordersRes.work_orders.length, page: 1, pages: 1, limit: 10, skip: 0 });
      }

      if (statsRes) {
        setStats({
          total: statsRes.total_work_orders || 0,
          completed: statsRes.by_status?.completed || 0,
          processing: statsRes.by_status?.processing || 0,
          pending: statsRes.by_status?.pending || 0,
          failed: statsRes.by_status?.failed || 0,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadComplete = (results) => {
    fetchData(0);
    const successResults = results.filter(r => r.success);
    if (successResults.length === 1 && successResults[0].work_order_id) {
      setShowUploadModal(false);
      navigate(`/gfs/v1/work-order/${successResults[0].work_order_id}`);
    }
  };

  const handleDelete = async (workOrderId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this work order?")) return;
    try {
      const token = localStorage.getItem("token");
      await fetch(`/v1/gfs/api/work-orders/${workOrderId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchData(pagination.skip);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      fetchData(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchData(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  const filteredWorkOrders = workOrders.filter((wo) => {
    const term = searchTerm.toLowerCase();
    return (
      wo._id?.toLowerCase().includes(term) ||
      wo.filename?.toLowerCase().includes(term) ||
      wo.uid?.toLowerCase().includes(term)
    );
  });

  const getStatusBadgeClass = (status) => {
    const classes = { completed: "status-completed", processing: "status-processing", pending: "status-pending", failed: "status-failed" };
    return classes[status] || "status-pending";
  };

  const getFormTypeLabel = (type) => {
    return FORM_TYPES.find(f => f.value === type)?.label || type;
  };

  return (
    <div className="gfs-v1-work-orders-list">
      {/* Header with Model Info */}
      <div className="gfs-v1-header-banner" style={{ borderLeftColor: MODEL_CONFIG.color }}>
        <div className="gfs-v1-header-info">
          <h2 style={{ color: MODEL_CONFIG.color }}>
            <Zap size={24} />
            {MODEL_CONFIG.name} Work Orders
          </h2>
          <p>{MODEL_CONFIG.description} - GFS Extraction Pipeline</p>
        </div>
        <div className="gfs-v1-header-actions">
          <button
            className="gfs-v1-btn-upload"
            onClick={() => setShowUploadModal(true)}
            style={{ backgroundColor: MODEL_CONFIG.color }}
          >
            <Upload size={18} /> Upload & Extract
          </button>
        </div>
      </div>

      {error && (
        <div className="gfs-v1-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="gfs-v1-stats-grid">
        <div className="gfs-v1-stat-card">
          <div className="gfs-v1-stat-value">{stats.total}</div>
          <div className="gfs-v1-stat-label">Total</div>
        </div>
        <div className="gfs-v1-stat-card completed">
          <div className="gfs-v1-stat-value">{stats.completed}</div>
          <div className="gfs-v1-stat-label">Completed</div>
        </div>
        <div className="gfs-v1-stat-card processing">
          <div className="gfs-v1-stat-value">{stats.processing}</div>
          <div className="gfs-v1-stat-label">Processing</div>
        </div>
        <div className="gfs-v1-stat-card pending">
          <div className="gfs-v1-stat-value">{stats.pending}</div>
          <div className="gfs-v1-stat-label">Pending</div>
        </div>
        <div className="gfs-v1-stat-card failed">
          <div className="gfs-v1-stat-value">{stats.failed}</div>
          <div className="gfs-v1-stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="gfs-v1-filters-row">
        <input
          type="text"
          placeholder="Search by ID, filename, or UID..."
          className="gfs-v1-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <CustomStatusDropdown value={statusFilter} onChange={setStatusFilter} />
        <button className="gfs-v1-btn-refresh" onClick={() => fetchData(0)}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="gfs-v1-table-container">
        <table className="gfs-v1-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Filename</th>
              <th>Form Type</th>
              <th>Status</th>
              <th>Pages</th>
              <th>Created</th>
              <th>Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="gfs-v1-table-loading">
                  <RefreshCw size={20} className="spin" /> Loading...
                </td>
              </tr>
            ) : filteredWorkOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="gfs-v1-table-empty">
                  No work orders found. Upload a document to get started.
                </td>
              </tr>
            ) : (
              filteredWorkOrders.map((wo) => (
                <tr key={wo._id} onClick={() => navigate(`/gfs/v1/work-order/${wo._id}`)}>
                  <td className="gfs-v1-id-cell">{wo._id?.slice(-8)}</td>
                  <td className="gfs-v1-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 30) || "-"}
                    {wo.filename?.length > 30 && "..."}
                  </td>
                  <td>
                    <span className="gfs-v1-form-badge" style={{ backgroundColor: MODEL_CONFIG.bgColor, color: MODEL_CONFIG.color }}>
                      {getFormTypeLabel(wo.form_type)}
                    </span>
                  </td>
                  <td>
                    <span className={`gfs-v1-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {wo.status}
                    </span>
                  </td>
                  <td>{wo.total_pages || "-"}</td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td>{wo.processing_time ? `${wo.processing_time.toFixed(1)}s` : "-"}</td>
                  <td className="gfs-v1-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button className="gfs-v1-action-btn view" title="View Details" onClick={() => navigate(`/gfs/v1/work-order/${wo._id}`)}>
                      <Eye size={16} />
                    </button>
                    <button
                      className="gfs-v1-action-btn json"
                      title="Download JSON"
                      onClick={(e) => {
                        e.stopPropagation();
                        const data = wo.extracted_data || {};
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${wo.filename?.replace(/\.[^/.]+$/, "") || wo._id}.json`;
                        a.click();
                      }}
                    >
                      <FileJson size={16} />
                    </button>
                    <button className="gfs-v1-action-btn delete" title="Delete" onClick={(e) => handleDelete(wo._id, e)}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="gfs-v1-pagination">
        <div className="gfs-v1-pagination-info">
          Showing {workOrders.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </div>
        <div className="gfs-v1-pagination-controls">
          <button onClick={handlePrevPage} disabled={pagination.page <= 1}>
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="gfs-v1-page-indicator">
            Page {pagination.page} of {pagination.pages || 1}
          </span>
          <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};

export default GFSV1WorkOrders;
